import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Heart, Activity, UserX, Calendar, Zap, CheckCircle2,
  Edit2, Shield, ExternalLink, TrendingDown,
} from 'lucide-react';
import { getTeamOverview, updateNextMatch } from '../api';
import { rpeValueColor } from './WellnessInput';

// ── Shared UI ─────────────────────────────────────────────────────────────────
const card = { background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 14, padding: '16px 18px' };
const cardTitle = { fontSize: 13, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 };
const emptyState = { fontSize: 13, color: 'var(--text-dimmed)', padding: '20px 0', textAlign: 'center' };
const RPE_COMPARISON_COLORS = { heavier: '#f59e0b', lighter: '#3b82f6', expected: '#10b981' };
const btn = (active, color = '#3b82f6') => ({
  padding: '6px 12px', borderRadius: 8, border: `1px solid ${active ? color : 'var(--border)'}`,
  background: active ? color : 'transparent', color: active ? '#fff' : 'var(--text-secondary)',
  fontSize: 12, fontWeight: active ? 600 : 400, cursor: 'pointer', fontFamily: 'inherit',
});
const inputStyle = {
  padding: '8px 10px', borderRadius: 8, border: '1px solid var(--border)',
  background: 'var(--bg-app)', color: 'var(--text-primary)', fontSize: 13,
  fontFamily: 'inherit', width: '100%', boxSizing: 'border-box',
};

function fmtDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('nl-NL', { weekday: 'long', day: '2-digit', month: 'long' });
}
// kickoffTime is a separate HH:MM:SS/HH:MM string when the match came from the Week
// Schedule (a DATE + TIME column, not one combined timestamp like the legacy next_match
// table). next_match has no separate time column at all — its time is embedded directly
// in dateStr, so that source still reads its time straight off the Date object. A schedule
// match with no kickoff entered yet just shows the date.
function fmtMatchDate(dateStr, kickoffTime, source) {
  if (!dateStr) return '—';
  const dt = new Date(dateStr);
  const datePart = dt.toLocaleDateString('nl-NL', { day: '2-digit', month: 'short' });
  if (kickoffTime) return `${datePart} · ${kickoffTime.slice(0, 5)}`;
  if (source === 'next_match') {
    return `${datePart} · ${dt.toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' })}`;
  }
  return datePart;
}

// ── FIX 1: balance the "missed targets" list toward the most extreme cases on
// both ends, capped at 6 total ──────────────────────────────────────────────
function pickDisplayedMissedTargets(flagged) {
  const below = flagged.filter(p => p.status === 'below').sort((a, b) => a.pctOfTarget - b.pctOfTarget);
  const above = flagged.filter(p => p.status === 'above').sort((a, b) => b.pctOfTarget - a.pctOfTarget);

  let displayed;
  if (flagged.length <= 6) {
    displayed = flagged;
  } else {
    const takeBelow = Math.min(3, below.length);
    const takeAbove = Math.min(3, above.length);
    let remaining = 6 - takeBelow - takeAbove;

    displayed = [...below.slice(0, takeBelow), ...above.slice(0, takeAbove)];

    if (remaining > 0) {
      const extraBelow = below.slice(takeBelow, takeBelow + remaining);
      displayed = [...displayed, ...extraBelow];
      remaining -= extraBelow.length;
      if (remaining > 0) {
        displayed = [...displayed, ...above.slice(takeAbove, takeAbove + remaining)];
      }
    }
  }

  const sorted = [...displayed].sort((a, b) => (a.status === b.status) ? 0 : a.status === 'below' ? -1 : 1);
  return { displayed: sorted, remainingCount: flagged.length - displayed.length };
}

// ── FIX 2: where the team average falls relative to the average target range ──
function targetBandStatus(avg, target) {
  if (!target || !target.lower || !target.upper) return null;
  const { lower, upper } = target;
  if (avg >= lower && avg <= upper) return { label: 'On target', color: '#10b981', band: 'on' };
  if (avg < lower) {
    const deficit = (lower - avg) / lower;
    return deficit <= 0.10
      ? { label: 'Slightly under', color: '#f59e0b', band: 'under' }
      : { label: 'Too low', color: '#ef4444', band: 'under' };
  }
  const excess = (avg - upper) / upper;
  return excess <= 0.10
    ? { label: 'Slightly over', color: '#f59e0b', band: 'over' }
    : { label: 'Too high', color: '#8b5cf6', band: 'over' };
}

function summarizeSession(statuses) {
  const valid = statuses.filter(Boolean);
  if (!valid.length) return null;
  const counts = { on: 0, under: 0, over: 0 };
  let hasTooHigh = false, hasTooLow = false;
  valid.forEach(s => { counts[s.band]++; if (s.label === 'Too high') hasTooHigh = true; if (s.label === 'Too low') hasTooLow = true; });
  const majority = Math.floor(valid.length / 2) + 1;
  if (counts.on >= majority) return { text: 'Session intensity: on target', color: '#10b981' };
  if (counts.under >= majority) return { text: 'Session was lighter than planned', color: hasTooLow ? '#ef4444' : '#f59e0b' };
  if (counts.over >= majority) return { text: 'Session ran heavier than planned', color: hasTooHigh ? '#8b5cf6' : '#f59e0b' };
  return { text: 'Session intensity: mixed results', color: '#94a3b8' };
}

// ── Alert pill ────────────────────────────────────────────────────────────────
function AlertPill({ icon: Icon, label, count, color, onClick }) {
  return (
    <button onClick={onClick} style={{
      display: 'inline-flex', alignItems: 'center', gap: 8, padding: '10px 16px', borderRadius: 99,
      border: `1px solid ${color}44`, background: count > 0 ? color + '14' : 'var(--bg-card)',
      color: count > 0 ? color : 'var(--text-muted)', cursor: 'pointer', fontFamily: 'inherit',
      fontSize: 13, fontWeight: 600,
    }}>
      <Icon size={15} />
      <span>{label}</span>
      <span style={{
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center', minWidth: 20, height: 20,
        borderRadius: 99, background: count > 0 ? color : 'var(--border)', color: count > 0 ? '#fff' : 'var(--text-dimmed)',
        fontSize: 11, fontWeight: 800, padding: '0 5px',
      }}>{count}</span>
    </button>
  );
}

// ── Row item (clickable list row) ──────────────────────────────────────────────
function Row({ onClick, children }) {
  return (
    <div onClick={onClick} style={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10,
      padding: '9px 0', borderTop: '1px solid var(--border)', cursor: onClick ? 'pointer' : 'default',
    }}>
      {children}
    </div>
  );
}

function Badge({ children, color }) {
  return (
    <span style={{
      fontSize: 11, fontWeight: 700, padding: '2px 9px', borderRadius: 99,
      background: color + '1c', color, border: `1px solid ${color}44`, whiteSpace: 'nowrap',
    }}>{children}</span>
  );
}

// ── Next match edit form ──────────────────────────────────────────────────────
function NextMatchEditForm({ nextMatch, user, onSave, onCancel }) {
  const toLocalInput = (d) => {
    if (!d) return '';
    const dt = new Date(d);
    const pad = n => String(n).padStart(2, '0');
    return `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}T${pad(dt.getHours())}:${pad(dt.getMinutes())}`;
  };
  const [opponent, setOpponent]   = useState(nextMatch?.opponent || '');
  const [matchDate, setMatchDate] = useState(toLocalInput(nextMatch?.match_date));
  const [isHome, setIsHome]       = useState(nextMatch?.is_home !== false);
  const [saving, setSaving]       = useState(false);

  const handleSave = async () => {
    if (!opponent.trim() || !matchDate) return;
    setSaving(true);
    try {
      await updateNextMatch({ opponent: opponent.trim(), match_date: matchDate, is_home: isHome, updated_by: user?.id });
      onSave();
    } finally { setSaving(false); }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <input style={inputStyle} placeholder="Opponent" value={opponent} onChange={e => setOpponent(e.target.value)} />
      <input style={inputStyle} type="datetime-local" value={matchDate} onChange={e => setMatchDate(e.target.value)} />
      <div style={{ display: 'flex', gap: 6 }}>
        <button style={btn(isHome, '#10b981')} onClick={() => setIsHome(true)}>Home</button>
        <button style={btn(!isHome, '#3b82f6')} onClick={() => setIsHome(false)}>Away</button>
      </div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 4 }}>
        <button style={btn(false)} onClick={onCancel}>Cancel</button>
        <button style={btn(true)} onClick={handleSave} disabled={saving || !opponent.trim() || !matchDate}>{saving ? 'Saving…' : 'Save'}</button>
      </div>
    </div>
  );
}

// ── Root export ───────────────────────────────────────────────────────────────
export default function Overview({ user, onNavigate }) {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [editingMatch, setEditingMatch] = useState(false);

  const refresh = useCallback(() => {
    getTeamOverview().then(setData).finally(() => setLoading(false));
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const goTo = (page, gpsTab) => onNavigate && onNavigate(page, gpsTab);

  const latestTrainingSession = data?.latestTrainingSession || null;

  const { displayed: missedTargetsDisplayed, remainingCount: missedTargetsRemaining } = useMemo(
    () => pickDisplayedMissedTargets(data?.missedTargets || []), [data]
  );

  const sessionMetricStatuses = useMemo(() => {
    if (!latestTrainingSession) return { td: null, hid: null, hmld: null };
    return {
      td:   targetBandStatus(latestTrainingSession.avg_td, latestTrainingSession.targets?.TD),
      hid:  targetBandStatus(latestTrainingSession.avg_hid, latestTrainingSession.targets?.HID),
      hmld: targetBandStatus(latestTrainingSession.avg_hmld, latestTrainingSession.targets?.HMLD),
    };
  }, [latestTrainingSession]);

  const sessionSummary = useMemo(
    () => summarizeSession([sessionMetricStatuses.td, sessionMetricStatuses.hid, sessionMetricStatuses.hmld]),
    [sessionMetricStatuses]
  );

  if (loading) {
    return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh', color: 'var(--text-muted)' }}>Loading team overview…</div>;
  }

  if (!data) {
    return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh', color: 'var(--text-dimmed)' }}>Unable to load team overview.</div>;
  }

  const {
    todayDate, todayMdLabel, todayScenario, unavailable, wellnessFlags, notSubmitted,
    nextMatch, squadAvailability, seasonAvailabilityPct,
    personalBests, recentRTC,
  } = data;

  const goodNewsCount = (personalBests?.length || 0) + (recentRTC?.length || 0);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18, maxWidth: 1200, margin: '0 auto' }}>

      {/* ── Header ── */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', flexWrap: 'wrap', gap: 10 }}>
        <div>
          <h2 style={{ fontSize: 22, fontWeight: 800, margin: 0, color: 'var(--text-primary)' }}>Team overview</h2>
          <div style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 4 }}>{fmtDate(todayDate)}</div>
        </div>
        {todayMdLabel && (
          <div style={{ fontSize: 13, color: 'var(--text-secondary)', background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 99, padding: '6px 14px' }}>
            {todayScenario ? `Scenario ${todayScenario} · ` : ''}<strong>{todayMdLabel}</strong>
          </div>
        )}
      </div>

      {/* ── Alert strip ── */}
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        <AlertPill icon={UserX}         label="Unavailable"     count={unavailable.length}    color="#ef4444" onClick={() => goTo('availability')} />
        <AlertPill icon={Heart}         label="Wellness flags"  count={wellnessFlags.length}  color="#f59e0b" onClick={() => goTo('wellnessstaff')} />
        <AlertPill icon={TrendingDown}  label="Missed targets"  count={(data?.missedTargets || []).length}  color="#f59e0b" onClick={() => goTo('gps', 'team-training')} />
        <AlertPill icon={Activity}      label="Not checked in"  count={notSubmitted.length}   color="#94a3b8" onClick={() => goTo('wellnessstaff')} />
      </div>

      {/* ── Row 1 ── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(360px, 1fr))', gap: 14 }}>
        <div style={card}>
          <div style={cardTitle}><UserX size={15} color="#ef4444" /> Unavailable today</div>
          {unavailable.length === 0 ? (
            <div style={emptyState}>All clear — full squad available.</div>
          ) : unavailable.map(p => (
            <Row key={p.player_id} onClick={() => goTo('medical')}>
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>{p.player_name}</div>
                <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{p.detail || p.reason}</div>
              </div>
              <Badge color={p.days_unavailable >= 7 ? '#ef4444' : '#f59e0b'}>{p.days_unavailable}d</Badge>
            </Row>
          ))}
        </div>

        <div style={card}>
          <div style={cardTitle}><Heart size={15} color="#f59e0b" /> Wellness flags today</div>
          {wellnessFlags.length === 0 ? (
            <div style={emptyState}>No flags — squad reporting normal wellness.</div>
          ) : wellnessFlags.map(p => (
            <Row key={p.player_id} onClick={() => goTo('wellnessstaff')}>
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>{p.player_name}</div>
                {!p.submittedToday && (
                  <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>⚠ Not submitted today — flags from yesterday</div>
                )}
              </div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                {p.flags.map((f, i) => (
                  <Badge key={i} color={p.submittedToday ? (f.severity === 'high' ? '#ef4444' : '#f59e0b') : '#64748b'}>{f.metric} {f.value}</Badge>
                ))}
              </div>
            </Row>
          ))}
        </div>
      </div>

      {/* ── Row 2 ── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(360px, 1fr))', gap: 14 }}>
        <div style={{ ...card, cursor: latestTrainingSession ? 'pointer' : 'default' }} onClick={() => latestTrainingSession && goTo('gps', 'team-training')}>
          <div style={cardTitle}><Activity size={15} color="#3b82f6" /> Latest training session</div>
          {!latestTrainingSession ? (
            <div style={emptyState}>No training sessions logged yet.</div>
          ) : (
            <div>
              <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)' }}>{latestTrainingSession.session_title || 'Training'}</div>
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>
                {fmtDate(latestTrainingSession.session_date)}{latestTrainingSession.md_label ? ` · ${latestTrainingSession.md_label}` : ''}
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10, marginTop: 14 }}>
                {[
                  { key: 'td',   label: 'Avg TD (m)',   value: latestTrainingSession.avg_td,   target: latestTrainingSession.targets?.TD,   status: sessionMetricStatuses.td },
                  { key: 'hid',  label: 'Avg HID (m)',  value: latestTrainingSession.avg_hid,  target: latestTrainingSession.targets?.HID,  status: sessionMetricStatuses.hid },
                  { key: 'hmld', label: 'Avg HMLD (m)', value: latestTrainingSession.avg_hmld, target: latestTrainingSession.targets?.HMLD, status: sessionMetricStatuses.hmld },
                ].map(m => (
                  <div key={m.key} style={{ background: 'var(--bg-app)', borderRadius: 10, padding: '12px 10px', textAlign: 'center' }}>
                    <div style={{ fontSize: 20, fontWeight: 800, color: 'var(--text-primary)' }}>{Math.round(m.value).toLocaleString()}</div>
                    <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>{m.label}</div>
                    {m.target && (
                      <div style={{ fontSize: 10, color: 'var(--text-dimmed)', marginTop: 4 }}>
                        target: {Math.round(m.target.lower).toLocaleString()}–{Math.round(m.target.upper).toLocaleString()}m
                      </div>
                    )}
                    {m.status && (
                      <div style={{ marginTop: 6 }}>
                        <Badge color={m.status.color}>{m.status.label}</Badge>
                      </div>
                    )}
                  </div>
                ))}
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 10, marginTop: 10 }}>
                {[
                  { key: 'rpe_m', label: 'Avg Muscular RPE',     value: latestTrainingSession.avg_rpe_muscular },
                  { key: 'rpe_c', label: 'Avg Cardiovascular RPE', value: latestTrainingSession.avg_rpe_cardiovascular },
                ].map(m => (
                  <div key={m.key} style={{ background: 'var(--bg-app)', borderRadius: 10, padding: '12px 10px', textAlign: 'center' }}>
                    <div style={{ fontSize: 20, fontWeight: 800, color: m.value != null ? rpeValueColor(m.value) : 'var(--text-dimmed)' }}>
                      {m.value != null ? m.value.toFixed(1) : '—'}
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>{m.label}</div>
                  </div>
                ))}
              </div>
              <div style={{ fontSize: 10, color: 'var(--text-dimmed)', marginTop: 6, textAlign: 'center' }}>
                RPE submitted by {latestTrainingSession.rpe_submitted} player{latestTrainingSession.rpe_submitted === 1 ? '' : 's'}
              </div>
              {latestTrainingSession.rpe_comparison && (
                <div style={{
                  marginTop: 8, fontSize: 12, fontWeight: 600, textAlign: 'center',
                  color: RPE_COMPARISON_COLORS[latestTrainingSession.rpe_comparison.tone],
                }}>
                  {latestTrainingSession.rpe_comparison.text}
                </div>
              )}
              {sessionSummary && (
                <div style={{ marginTop: 12, fontSize: 12, fontWeight: 700, color: sessionSummary.color, textAlign: 'center' }}>
                  {sessionSummary.text}
                </div>
              )}
            </div>
          )}
        </div>

        <div style={card}>
          <div style={cardTitle}><TrendingDown size={15} color="#ef4444" /> Missed targets last session</div>
          {missedTargetsDisplayed.length === 0 ? (
            <div style={{ ...emptyState, color: '#10b981' }}>No deviations — team on target ✓</div>
          ) : (
            <>
              {missedTargetsDisplayed.map((p, i) => (
                <Row key={`${p.player_id}-${i}`} onClick={() => goTo('gps', 'team-training')}>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>{p.player_name}</div>
                    <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{p.metric}{p.pctOfTarget != null ? ` · ${p.pctOfTarget}% of target` : ''}</div>
                  </div>
                  <Badge color={p.status === 'below' ? '#ef4444' : '#7c3aed'}>{p.status === 'below' ? 'Below' : 'Above'}</Badge>
                </Row>
              ))}
              {missedTargetsRemaining > 0 && (
                <div style={{ marginTop: 10, textAlign: 'center' }}>
                  <button onClick={() => goTo('gps', 'team-training')} style={{ background: 'none', border: 'none', color: '#60a5fa', fontSize: 12, cursor: 'pointer', fontFamily: 'inherit' }}>
                    +{missedTargetsRemaining} more — view full Team Training
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* ── Row 3 ── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(360px, 1fr))', gap: 14 }}>
        <div style={card}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={cardTitle}><Calendar size={15} color="#3b82f6" /> Next match</div>
            {/* The schedule is now the source of truth once a match day has an opponent
                typed in — editing here would silently do nothing, so the pencil (which
                only ever edits the legacy next_match fallback) hides in that case. */}
            {user?.isAdmin && !editingMatch && nextMatch?.source !== 'schedule' && (
              <button onClick={() => setEditingMatch(true)} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', padding: 4 }}>
                <Edit2 size={14} />
              </button>
            )}
          </div>

          {editingMatch ? (
            <NextMatchEditForm
              nextMatch={nextMatch} user={user}
              onSave={() => { setEditingMatch(false); refresh(); }}
              onCancel={() => setEditingMatch(false)}
            />
          ) : nextMatch ? (
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                {nextMatch.logo_url && (
                  <img src={nextMatch.logo_url} alt="" style={{ width: 28, height: 28, objectFit: 'contain', borderRadius: 5 }} />
                )}
                <div style={{ fontSize: 17, fontWeight: 700, color: 'var(--text-primary)' }}>
                  {nextMatch.is_home ? 'vs' : '@'} {nextMatch.opponent}
                </div>
              </div>
              <div style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 4 }}>
                {fmtMatchDate(nextMatch.match_date, nextMatch.kickoff_time, nextMatch.source)}
              </div>
              <Badge color={nextMatch.is_home ? '#10b981' : '#3b82f6'}>{nextMatch.is_home ? 'Home' : 'Away'}</Badge>
              <a href="https://www.heracles.nl/team/wedstrijden/" target="_blank" rel="noopener noreferrer"
                style={{ display: 'inline-flex', alignItems: 'center', gap: 5, marginTop: 14, fontSize: 12, color: '#60a5fa', textDecoration: 'none' }}>
                Full schedule <ExternalLink size={11} />
              </a>
            </div>
          ) : (
            <div>
              <div style={emptyState}>No upcoming match set.</div>
              <a href="https://www.heracles.nl/team/wedstrijden/" target="_blank" rel="noopener noreferrer"
                style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12, color: '#60a5fa', textDecoration: 'none' }}>
                Full schedule <ExternalLink size={11} />
              </a>
            </div>
          )}
        </div>

        <div style={card}>
          <div style={cardTitle}><Shield size={15} color="#10b981" /> Squad availability</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 }}>
            <div style={{ background: 'var(--bg-app)', borderRadius: 10, padding: '12px 10px', textAlign: 'center' }}>
              <div style={{ fontSize: 24, fontWeight: 800, color: '#10b981' }}>{squadAvailability.available}</div>
              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>Available</div>
            </div>
            <div style={{ background: 'var(--bg-app)', borderRadius: 10, padding: '12px 10px', textAlign: 'center' }}>
              <div style={{ fontSize: 24, fontWeight: 800, color: '#ef4444' }}>{squadAvailability.unavailable}</div>
              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>Unavailable</div>
            </div>
            <div style={{ background: 'var(--bg-app)', borderRadius: 10, padding: '12px 10px', textAlign: 'center' }}>
              <div style={{ fontSize: 24, fontWeight: 800, color: '#3b82f6' }}>{seasonAvailabilityPct != null ? `${seasonAvailabilityPct}%` : '—'}</div>
              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>Season avail.</div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Good news ── */}
      <div style={card}>
        <div style={cardTitle}><Zap size={15} color="#eab308" /> Good news this week</div>
        {goodNewsCount === 0 ? (
          <div style={emptyState}>No updates this week.</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {personalBests.map((p, i) => (
              <Row key={`pb-${i}`} onClick={() => goTo('gps', 'max-speed')}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <Zap size={15} color="#eab308" />
                  <span style={{ fontSize: 13, color: 'var(--text-primary)', fontWeight: 600 }}>{p.player_name}</span>
                  <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>new personal best — {Number(p.max_speed).toFixed(1)} km/h</span>
                </div>
              </Row>
            ))}
            {recentRTC.map((p, i) => (
              <Row key={`rtc-${i}`} onClick={() => goTo('medical')}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <CheckCircle2 size={15} color="#10b981" />
                  <span style={{ fontSize: 13, color: 'var(--text-primary)', fontWeight: 600 }}>{p.player_name}</span>
                  <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>moved to Return to Competition</span>
                </div>
              </Row>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
